import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  OnboardingPlan,
  GetPlansResponse,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
} from '@dreamai/types';
import api from '../../../api';
import { ReactComponent as CloseIcon } from '../../../icons/close-modal.svg';
import './plan-selection-modal.scss';

interface PlanSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPriceId?: string;
}

/** Convert hours to a human-friendly label + unit (e.g. 24 → "1 day") */
function formatHours(hours: number): { value: string; unit: string } {
  if (hours >= 24 && hours % 24 === 0) {
    const days = hours / 24;
    return { value: String(days), unit: days === 1 ? 'Day' : 'Days' };
  }
  return { value: String(hours), unit: hours === 1 ? 'Hour' : 'Hours' };
}

/** Format a price amount with currency symbol (includes 20% VAT) */
function formatPrice(amount: number, currency: string): string {
  const symbols: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' };
  const sym = symbols[currency] || currency + ' ';
  const withTax = amount * 1.2;
  const rounded = Math.round(withTax);
  return `${sym}${rounded}`;
}

/** Build features list from plan metadata */
function buildFeatures(plan: OnboardingPlan) {
  const features: Array<{ title: string; description: string }> = [
    {
      title: 'All tasks available',
      description:
        "Your assistant can handle any task that you'd expect a Personal Assistant to take take on.",
    },
  ];

  if (plan.purchaseLimit != null) {
    const limit = plan.purchaseLimit.toLocaleString('en-GB');
    features.push({
      title: `£${limit} purchase limit`,
      description: `Your PA can buy things on your behalf, up to a £${limit} limit`,
    });
  }

  if (plan.agentEmail) {
    features.push({
      title: 'Dedicated email for your PA',
      description: 'A custom email for your PA to plan things on your behalf.',
    });
  }

  return features;
}

const CheckCircleIcon: FC = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#489fd9'
    strokeWidth='2'
  >
    <circle cx='12' cy='12' r='9' />
    <path d='M9 12l2 2 4-4' />
  </svg>
);

const CheckIcon: FC = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#489fd9'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M5 13l4 4L19 7' />
  </svg>
);

const PlanSelectionModal: FC<PlanSelectionModalProps> = ({
  isOpen,
  onClose,
  currentPriceId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [plans, setPlans] = useState<OnboardingPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [submittingPlanId, setSubmittingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingPlans(true);
    setError(null);

    const fetchPlans = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetPlansResponse>('/onboarding/plans', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success && res.data.plans.length > 0) {
          setPlans(res.data.plans);
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError('Failed to load plans. Please try again.');
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [isOpen, getAccessTokenSilently]);

  const handleChoosePlan = useCallback(
    async (plan: OnboardingPlan) => {
      setSubmittingPlanId(plan.priceId);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const body: CreateCheckoutRequest = {
          priceId: plan.priceId,
          returnUrl: window.location.origin,
        };

        const response = await api.post<CreateCheckoutResponse>(
          '/onboarding/create-checkout',
          body,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        window.location.href = response.data.checkoutUrl;
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            'Failed to create checkout. Please try again.',
        );
        setSubmittingPlanId(null);
      }
    },
    [getAccessTokenSilently],
  );

  if (!isOpen) return null;

  return (
    <div className='plan-selection-modal-overlay' onClick={onClose}>
      <div
        className='plan-selection-modal'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <h2>Select a plan</h2>
          <button className='close-button' onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className='modal-body'>
          {loadingPlans ? (
            <div className='plan-loading'>Loading plans...</div>
          ) : error && plans.length === 0 ? (
            <div className='plan-error'>{error}</div>
          ) : (
            <div className='plans-grid'>
              {plans.map((plan, planIndex) => {
                const ai = formatHours(plan.aiHours);
                const human = formatHours(plan.humanHours);
                const features = buildFeatures(plan);
                const isSubmitting = submittingPlanId === plan.priceId;
                const isHighlighted = planIndex === 1;
                const isCurrent = currentPriceId === plan.priceId;

                return (
                  <div
                    key={plan.priceId}
                    className={`plan-card${isHighlighted ? ' highlighted' : ''}${isCurrent ? ' current' : ''}`}
                  >
                    <div className='plan-card-header'>
                      <h3 className='plan-name'>{plan.name}</h3>
                      {plan.description && (
                        <p className='plan-tagline'>{plan.description}</p>
                      )}
                      <p className='plan-price'>
                        <span className='price-amount'>
                          {formatPrice(plan.amount, plan.currency)}
                        </span>
                        <span className='price-period'>
                          /{plan.periodUnit.toLowerCase()}
                        </span>
                      </p>
                      <button
                        className={`choose-plan-button${isCurrent ? ' current-plan' : ''}`}
                        onClick={
                          isCurrent ? undefined : () => handleChoosePlan(plan)
                        }
                        disabled={isCurrent || submittingPlanId !== null}
                      >
                        {isCurrent
                          ? 'Current Plan'
                          : isSubmitting
                            ? 'Loading...'
                            : `Choose ${plan.name}`}
                      </button>
                    </div>

                    <div className='plan-time-badges'>
                      <div className='time-pill ai'>
                        <div className='pill-number'>{ai.value}</div>
                        <div className='pill-text'>
                          <span className='pill-unit'>{ai.unit}</span>
                          <span className='pill-label'>AI</span>
                        </div>
                      </div>
                      <span className='time-plus'>+</span>
                      <div className='time-pill human'>
                        <div className='pill-number'>{human.value}</div>
                        <div className='pill-text'>
                          <span className='pill-unit'>{human.unit}</span>
                          <span className='pill-label'>Human</span>
                        </div>
                      </div>
                    </div>

                    <div className='plan-features'>
                      {features.map((feature, i) => (
                        <div key={i} className='feature-item'>
                          <CheckCircleIcon />
                          <div className='feature-text'>
                            <p className='feature-title'>{feature.title}</p>
                            <p className='feature-description'>
                              {feature.description}
                            </p>
                          </div>
                        </div>
                      ))}

                      <div className='also-included'>
                        <p className='also-included-title'>Also included:</p>
                        <div className='included-item'>
                          <CheckIcon />
                          <span>Easily top-up your AI and Human time</span>
                        </div>
                        <div className='included-item'>
                          <CheckIcon />
                          <span>Cancel anytime</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {error && plans.length > 0 && <p className='plan-error'>{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionModal;
